import React, { useState, useRef } from 'react'
import { Popover, PopoverBody } from 'reactstrap'
import moment from 'moment'
import { FaChevronDown } from 'react-icons/fa'
import CloseIcon from '../../../assets/icons/icon-popover-close.svg'
import { LEVEL } from './utils/constants/levels'
import { ARTICLE_PATHS } from './utils/constants/articleUrls'
import { resolveUrlForArticles } from './utils/urlUtils'
import { formatRemainingTime } from './utils/displayUtils'
import { useMembershipJourneyContext } from './MembershipJourneyContext'
import {
  LevelProgressLink,
  PopoverContainer,
  PopoverHeader,
  PopoverTitle,
  PopoverCloseButton,
  PopoverLevelSection,
  PopoverLevelTitle,
  PopoverLevelContent,
  PopoverDateRow,
  PopoverDateLabel,
  PopoverDateValue,
  PopoverNotStarted,
  PopoverDivider,
  PopoverButton,
  PopoverButtonText,
  PopoverGlobalStyles,
  StatusBadgeCheckmark
} from './Styles/ProgressTracker.styles'

const LEVEL_NAMES = {
  [LEVEL.QUALIFYING]: 'Qualifying',
  [LEVEL.LEVEL_1]: 'Level 1',
  [LEVEL.LEVEL_2]: 'Level 2',
  [LEVEL.LEVEL_3]: 'Level 3'
}

const formatDisplayDate = (dateString) => {
  if (!dateString) return 'Not Started'
  return moment(dateString).format('MM/DD/YYYY')
}

const LevelProgressPopover = () => {
  const [popoverOpen, setPopoverOpen] = useState(false)
  const targetRef = useRef(null)
  const { pgmProgress } = useMembershipJourneyContext()
  const levels = pgmProgress?.levels || []

  const toggle = () => setPopoverOpen(!popoverOpen)

  // Build all three levels from pgmProgress
  const levelKeys = [LEVEL.LEVEL_1, LEVEL.LEVEL_2, LEVEL.LEVEL_3]
  const levelsData = levelKeys.map(levelKey => {
    const levelData = levels.find(l => l.key === levelKey)
    
    if (levelData) {
      const isCompleted = levelData.status === 'complete'
      const isStarted = levelData.status !== 'locked'
      
      return {
        levelKey,
        levelName: LEVEL_NAMES[levelKey],
        isCompleted,
        isStarted,
        startDate: levelData.startDate,
        completionDate: levelData.completionDate,
        expirationDate: levelData.expirationDate
      }
    } else {
      return {
        levelKey,
        levelName: LEVEL_NAMES[levelKey],
        isCompleted: false,
        isStarted: false
      }
    }
  })

  const level1Data = levelsData.find(p => p.levelKey === LEVEL.LEVEL_1)
  const level3Data = levelsData.find(p => p.levelKey === LEVEL.LEVEL_3)
  const allLevelsComplete = levelsData.every(level => level.isCompleted)
  
  const associateProgram = {
    isCompleted: allLevelsComplete,
    startDate: level1Data?.startDate,
    completedDate: allLevelsComplete && level3Data?.completionDate ? level3Data.completionDate : null,
    expirationDate: pgmProgress?.programExpirationDate
  }

  return (
    <>
      <PopoverGlobalStyles />
      <LevelProgressLink
        as="button"
        ref={targetRef}
        onClick={toggle}
        id="datesProgressPopover"
      >
        View My Deadlines
        <FaChevronDown style={{ width: 10, height: 10 }} />
      </LevelProgressLink>

      <Popover
        placement="bottom-end"
        isOpen={popoverOpen}
        target={targetRef}
        toggle={toggle}
        trigger="legacy"
        modifiers={{
          preventOverflow: { enabled: false },
          flip: { enabled: false }
        }}
      >
        <PopoverBody>
          <PopoverContainer>
            <PopoverHeader>
              <PopoverTitle>Associate Program Dates</PopoverTitle>
              <PopoverCloseButton onClick={toggle} aria-label="Close">
                <img src={CloseIcon} alt="" />
              </PopoverCloseButton>
            </PopoverHeader>

            {levelsData.map((level, index) => {
              const isLastLevel = index === levelsData.length - 1
              const timeRemaining = formatRemainingTime(level.expirationDate)
              
              return (
                <React.Fragment key={level.levelKey}>
                  <PopoverLevelSection>
                    <PopoverLevelTitle>
                      {level.isCompleted && <StatusBadgeCheckmark color="#2CA24B" />}
                      {level.levelName}
                    </PopoverLevelTitle>
                    <PopoverLevelContent>
                      {level.isStarted ? (
                        <>
                          <PopoverDateRow>
                            <PopoverDateLabel>Started:</PopoverDateLabel>
                            <PopoverDateValue>
                              {formatDisplayDate(level.startDate)}
                            </PopoverDateValue>
                          </PopoverDateRow>
                          
                          {level.isCompleted ? (
                            <PopoverDateRow>
                              <PopoverDateLabel isCompleted>Completed:</PopoverDateLabel>
                              <PopoverDateValue isCompleted>
                                {formatDisplayDate(level.completionDate)}
                              </PopoverDateValue>
                            </PopoverDateRow>
                          ) : (
                            <>
                              <PopoverDateRow>
                                <PopoverDateLabel>Deadline:</PopoverDateLabel>
                                <PopoverDateValue>
                                  {formatDisplayDate(level.expirationDate)}
                                </PopoverDateValue>
                              </PopoverDateRow>
                              {timeRemaining && (
                                <PopoverDateRow>
                                  <PopoverDateLabel>Time Remaining:</PopoverDateLabel>
                                  <PopoverDateValue>
                                    {timeRemaining}
                                  </PopoverDateValue>
                                </PopoverDateRow>
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        <PopoverNotStarted>Not Started</PopoverNotStarted>
                      )}
                    </PopoverLevelContent>
                  </PopoverLevelSection>
                  {!isLastLevel && <PopoverDivider />}
                </React.Fragment>
              )
            })}
            <PopoverDivider />

            <PopoverLevelSection isLastSection>
              <PopoverLevelTitle>
                {associateProgram.isCompleted && <StatusBadgeCheckmark color="#2CA24B" />}
                Election to Membership Deadline
              </PopoverLevelTitle>
              <PopoverLevelContent>
                {associateProgram.startDate && (
                  <PopoverDateRow>
                    <PopoverDateLabel>Started:</PopoverDateLabel>
                    <PopoverDateValue>
                      {formatDisplayDate(associateProgram.startDate)}
                    </PopoverDateValue>
                  </PopoverDateRow>
                )}
                {associateProgram.isCompleted ? (
                  <PopoverDateRow>
                    <PopoverDateLabel isCompleted>Completed:</PopoverDateLabel>
                    <PopoverDateValue isCompleted>
                      {formatDisplayDate(associateProgram.completedDate)}
                    </PopoverDateValue>
                  </PopoverDateRow>
                ) : (
                  <>
                    {associateProgram.expirationDate && (
                      <PopoverDateRow>
                        <PopoverDateLabel>Deadline:</PopoverDateLabel>
                        <PopoverDateValue>
                          {formatDisplayDate(associateProgram.expirationDate)}
                        </PopoverDateValue>
                      </PopoverDateRow>
                    )}
                  </>
                )}
              </PopoverLevelContent>
            </PopoverLevelSection>

            <PopoverButton
              href={resolveUrlForArticles(ARTICLE_PATHS.TIMEFRAME_DEADLINES)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <PopoverButtonText>Acceptable Progress Guide</PopoverButtonText>
            </PopoverButton>
          </PopoverContainer>
        </PopoverBody>
      </Popover>
    </>
  )
}

export default LevelProgressPopover

